import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { routes } from '#apps/client-web/app/routes';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditId } from '#packages/data-collection/models/audit';
import { type AuditLightingTypeForm } from '#packages/data-collection/models/audit-lighting-type-form';

export type AuditFormLightingTypePageViewModel = ReturnType<
  typeof useAuditFormLightingTypePageViewModel
>;

type AuditFormLightingTypePageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormLightingTypePageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormLightingTypePageViewModelProps) => {
  const { auditId } = useParams<{ auditId: AuditId }>();
  const navigate = useNavigate();

  const auditFormQuery = useLoadAuditFormByType<AuditLightingTypeForm>({
    auditId: auditId!,
    queryClient,
    formType: 'lighting-type',
    auditFormService,
  });

  // Collect the first attachment fileId from each entry (in order)
  const thumbnailFileIds = useMemo(
    () =>
      (auditFormQuery.data?.entries ?? []).map(
        (entry) => entry.attachments?.[0]?.name ?? null,
      ),
    [auditFormQuery.data?.entries],
  );

  const uniqueThumbnailFileIds = useMemo(
    () =>
      Array.from(
        new Set(thumbnailFileIds.filter((id): id is string => id !== null)),
      ),
    [thumbnailFileIds],
  );

  const thumbnailQueries = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds: uniqueThumbnailFileIds,
  });

  const thumbnailMap = useMemo<Record<string, FileContent | null>>(() => {
    const map: Record<string, FileContent | null> = {};
    uniqueThumbnailFileIds.forEach((fileId, i) => {
      map[fileId] = thumbnailQueries[i]?.data ?? null;
    });
    return map;
  }, [uniqueThumbnailFileIds, thumbnailQueries]);

  const onCreate = useCallback(() => {
    logger?.info('Navigating to create lighting type form entry page');
    navigate(
      routes.audit.getEnergyAuditLightingTypeFormEntryPath(auditId!, -1),
    );
  }, [navigate, auditId]);

  return {
    auditId,
    auditFormQuery,
    thumbnailFileIds,
    thumbnailMap,
    onCreate,
  };
};
